import { useEffect, useState, type ReactNode } from "react";
import { lettersApi, ApiError } from "@/api";
import type { LetterDetail, LetterSource } from "@/types";
import { Button, Spinner, Pill } from "@/components/ui";
import {
  humanise,
  flagLabel,
  decisionTone,
  letterConfidenceTone,
  letterSourceLabel,
  letterSourceTone,
} from "@/lib/jobs";
import { formatDateShort } from "@/lib/format";
import { FormDrawer } from "./FormDrawer";

/* ── value helpers (raw_json is best-effort across schema versions) ──────── */

const DATE_RE = /^\d{4}-\d{2}-\d{2}/;
const CODE_RE = /^[a-z0-9]+(_[a-z0-9]+)+$/;

function asArr(v: unknown): Record<string, unknown>[] {
  return Array.isArray(v) ? (v.filter((x) => x && typeof x === "object") as Record<string, unknown>[]) : [];
}
function asObj(v: unknown): Record<string, unknown> {
  return v && typeof v === "object" && !Array.isArray(v) ? (v as Record<string, unknown>) : {};
}
function asStr(v: unknown): string | null {
  if (typeof v === "string") return v.trim() || null;
  if (typeof v === "number") return String(v);
  return null;
}
/** Display a scalar value: format dates, humanise snake_case codes, Yes for true. */
function renderScalar(v: unknown): string | null {
  if (v === null || v === undefined || v === "") return null;
  if (typeof v === "boolean") return v ? "Yes" : null; // hide false
  if (typeof v === "number") return String(v);
  if (typeof v === "string") {
    const s = v.trim();
    if (!s) return null;
    if (DATE_RE.test(s)) return formatDateShort(s);
    if (CODE_RE.test(s)) return humanise(s);
    return s;
  }
  return null;
}

/* ── section components — each renders nothing when empty ────────────────── */

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="letter-section">
      <h4>{title}</h4>
      {children}
    </section>
  );
}

/** A definition list of the scalar, meaningful entries in a record. */
function KeyValueSection({ title, data }: { title: string; data: unknown }) {
  const obj = asObj(data);
  const rows = Object.entries(obj)
    .map(([k, v]) => [k, renderScalar(v)] as const)
    .filter(([, v]) => v != null);
  if (rows.length === 0) return null;
  return (
    <Section title={title}>
      <dl className="letter-kv">
        {rows.map(([k, v]) => (
          <div key={k}>
            <dt>{humanise(k)}</dt>
            <dd>{v}</dd>
          </div>
        ))}
      </dl>
    </Section>
  );
}

/** A list of object items — primary line + a muted line of the rest. */
function ItemListSection({ title, items }: { title: string; items: unknown }) {
  const list = asArr(items);
  if (list.length === 0) return null;
  const PRIMARY_KEYS = ["title", "action", "description", "summary", "ref", "item_ref"];
  return (
    <Section title={title}>
      <ul className="letter-items">
        {list.map((item, i) => {
          const primaryKey = PRIMARY_KEYS.find((k) => asStr(item[k]));
          const primary = primaryKey ? asStr(item[primaryKey]) : `Item ${i + 1}`;
          const details = Object.entries(item)
            .filter(([k]) => k !== primaryKey)
            .map(([k, v]) => [k, renderScalar(v)] as const)
            .filter(([, v]) => v != null)
            .map(([k, v]) => `${humanise(k)}: ${v}`);
          return (
            <li key={i}>
              <span className="letter-item__primary">{primary}</span>
              {details.length > 0 ? <span className="letter-item__detail">{details.join(" · ")}</span> : null}
            </li>
          );
        })}
      </ul>
    </Section>
  );
}

/** Flags in the Active-Flags convention: solid for true, outline for "unknown". */
function FlagSection({ flags }: { flags: unknown }) {
  const obj = asObj(flags);
  const entries = Object.entries(obj).filter(([, v]) => v === true || v === "unknown");
  if (entries.length === 0) return null;
  return (
    <Section title="Flags">
      <div className="job-flags">
        {entries.map(([key, v]) => {
          const unknown = v === "unknown";
          return (
            <span key={key} className={`job-flag${unknown ? " job-flag--unknown" : ""}`} title={unknown ? "Unknown — not confirmed" : undefined}>
              {flagLabel(key)}{unknown ? " ?" : ""}
            </span>
          );
        })}
      </div>
    </Section>
  );
}

function LinkSection({ links }: { links: unknown }) {
  const obj = asObj(links);
  const entries = Object.entries(obj).filter(([, v]) => typeof v === "string" && /^https?:\/\//.test(v));
  if (entries.length === 0) return null;
  return (
    <Section title="Links">
      <div className="letter-links">
        {entries.map(([k, v]) => (
          <a key={k} href={v as string} target="_blank" rel="noreferrer" className="job-invoice__link">
            {humanise(k.replace(/_url$/, ""))} ↗
          </a>
        ))}
      </div>
    </Section>
  );
}

function DecisionSection({ decision }: { decision: unknown }) {
  const obj = asObj(decision);
  const outcome = asStr(obj.outcome);
  const text = asStr(obj.decision_text);
  const basis = asStr(obj.legislative_basis);
  const rejections = asArr(obj.rejection_reasons);
  if (!outcome && !text && rejections.length === 0) return null;
  return (
    <Section title="Decision">
      {outcome ? <Pill tone={decisionTone(outcome)}>{humanise(outcome)}</Pill> : null}
      {text ? <p className="letter-prose">{text}</p> : null}
      {rejections.length > 0 ? (
        <ul className="letter-items">
          {rejections.map((r, i) => (
            <li key={i}><span className="letter-item__primary">{asStr(r.reason) || asStr(r.summary) || asStr(r.description) || `Reason ${i + 1}`}</span></li>
          ))}
        </ul>
      ) : null}
      {basis ? <p className="job-drawer__muted">{basis}</p> : null}
    </Section>
  );
}

/* ── drawer ──────────────────────────────────────────────────────────────── */

export function LetterDetailDrawer({
  id,
  source,
  onClose,
  userId,
  isRead,
  onReadChange,
}: {
  id: number;
  source: LetterSource;
  onClose: () => void;
  /** Inbox usage: passing the Xano user.id enables read-receipts
   *  (mark-read-on-open + manual toggle). The job-detail usage omits it and
   *  stays read-only. */
  userId?: number;
  isRead?: boolean;
  onReadChange?: (read: boolean) => void;
}) {
  const [letter, setLetter] = useState<LetterDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | undefined>();
  const [read, setRead] = useState(!!isRead);
  const [marking, setMarking] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    lettersApi
      .getLetter(id, source, userId)
      .then((d) => {
        if (!cancelled) setLetter(d);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof ApiError ? err.message : "Could not load the letter.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [id, source, userId]);

  // Mark read on open (inbox only: receipts enabled + currently unread).
  useEffect(() => {
    if (!userId || isRead) return;
    let cancelled = false;
    lettersApi
      .markRead(source, id, userId)
      .then(() => {
        if (cancelled) return;
        setRead(true);
        onReadChange?.(true);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
    // onReadChange intentionally omitted — markRead is idempotent.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, source, userId, isRead]);

  async function toggleRead() {
    if (!userId || marking) return;
    setMarking(true);
    try {
      if (read) {
        await lettersApi.markUnread(source, id, userId);
        setRead(false);
        onReadChange?.(false);
      } else {
        await lettersApi.markRead(source, id, userId);
        setRead(true);
        onReadChange?.(true);
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not update the read state.");
    } finally {
      setMarking(false);
    }
  }

  const rj = letter?.raw_json ?? {};
  const isPlanning = source === "planning";
  const linkedTask = letter?.linked_task && !letter.linked_task.deleted_at ? letter.linked_task : null;

  return (
    <FormDrawer title="Letter" onClose={onClose} footer={<Button variant="ghost" onClick={onClose}>Close</Button>}>
      {error ? <p className="leads__error" role="alert" onClick={() => setError(undefined)}>{error}</p> : null}

      {loading || !letter ? (
        !error ? <div className="jobs-list__loading"><Spinner label="Loading letter…" /></div> : null
      ) : (
        <>
          {/* Header */}
          <div className="letter-head">
            <div className="letter-head__badges">
              <Pill tone={letterSourceTone(letter.source)}>{letterSourceLabel(letter.source)}</Pill>
              {letter.confidence ? <Pill tone={letterConfidenceTone(letter.confidence)}>{humanise(letter.confidence)} confidence</Pill> : null}
              {userId ? (
                <button type="button" className="letter-read-toggle" onClick={toggleRead} disabled={marking}>
                  {read ? "Mark unread" : "Mark read"}
                </button>
              ) : null}
            </div>
            <h3 className="letter-head__title">
              {humanise(letter.letter_type)}
              {letter.letter_subtype ? ` · ${humanise(letter.letter_subtype)}` : ""}
            </h3>
            <p className="job-drawer__muted">
              {formatDateShort(letter.letter_date)}
              {letter.addressed_to ? ` · To ${letter.addressed_to}` : ""}
              {letter.addressed_to_role ? ` (${humanise(letter.addressed_to_role)})` : ""}
            </p>
            {letter.source_document_title ? <p className="letter-prose">{letter.source_document_title}</p> : null}
            {letter.pdf_url ? (
              <a href={letter.pdf_url} target="_blank" rel="noreferrer" download={letter.pdf_filename ?? undefined} className="job-invoice__link">
                View / download PDF ↗
              </a>
            ) : null}
          </div>

          {/* Context */}
          {letter.application || letter.job || linkedTask ? (
            <div className="letter-context">
              {letter.application ? (
                <p className="job-drawer__muted">
                  Application: {letter.application.app_reference || "Draft"}
                  {letter.application.application_type ? ` · ${humanise(letter.application.application_type)}` : ""}
                  {letter.application.status ? ` · ${humanise(letter.application.status)}` : ""}
                  {letter.application.local_authority_or_bcb ? ` · ${letter.application.local_authority_or_bcb}` : ""}
                </p>
              ) : null}
              {letter.job ? (
                <p className="job-drawer__muted">Job: {letter.job.job_number} — {letter.job.job_name || letter.job.full_address || "Untitled"}</p>
              ) : null}
              {linkedTask ? (
                <p className="job-drawer__muted">
                  Linked task: {linkedTask.title} · {humanise(linkedTask.status)} · {humanise(linkedTask.priority)}
                  {linkedTask.due_date ? ` · due ${formatDateShort(linkedTask.due_date)}` : ""}
                </p>
              ) : null}
            </div>
          ) : null}

          {/* Parsed analysis (branched by source; each block hides when empty) */}
          <DecisionSection decision={rj.decision} />
          <KeyValueSection title="Key dates" data={rj.dates} />
          <ItemListSection title="Conditions" items={rj.conditions} />
          <ItemListSection title="Outstanding items" items={rj.outstanding_items} />
          {isPlanning ? <ItemListSection title="Questions from the planner" items={rj.questions_from_planner} /> : null}
          {!isPlanning ? <ItemListSection title="Design review items" items={rj.design_review_items} /> : null}
          {!isPlanning ? <ItemListSection title="Inspection records" items={rj.inspection_records} /> : null}
          <ItemListSection title="Informatives" items={rj.informatives} />
          <ItemListSection title="Next actions" items={rj.next_actions} />

          {/* Approved documents */}
          <ItemListSection title="Approved drawings" items={asObj(rj.approved_documents).drawings} />
          {!isPlanning ? <ItemListSection title="Calculations" items={asObj(rj.approved_documents).calculations} /> : null}
          <ItemListSection title="Supporting documents" items={asObj(rj.approved_documents).supporting_documents} />

          {/* Key-value sections */}
          <KeyValueSection title="Fee" data={rj.fee} />
          {isPlanning ? <KeyValueSection title="CIL" data={rj.cil} /> : null}
          {!isPlanning ? <KeyValueSection title="Regulation parts" data={rj.regulation_parts} /> : null}
          {!isPlanning ? <KeyValueSection title="Inspection stages" data={rj.inspection_stages} /> : null}
          {!isPlanning ? <KeyValueSection title="Duty holders" data={rj.duty_holders} /> : null}
          {!isPlanning ? <KeyValueSection title="Energy compliance" data={rj.energy_compliance} /> : null}
          {isPlanning ? <KeyValueSection title="Appeal rights" data={rj.appeal_rights} /> : null}
          <KeyValueSection title="Working hours restriction" data={rj.working_hours_restriction} />

          <FlagSection flags={rj.flags} />
          <LinkSection links={rj.links} />
        </>
      )}
    </FormDrawer>
  );
}
